// src/pages/NewMenu.jsx
import React, { useEffect, useState } from "react";
import { FaLayerGroup, FaPlus, FaSave, FaList, FaTrash } from "react-icons/fa";
import {
  loadSuperAppConfig,
  getSuperAppConfigSync,
  setSuperAppConfig,
  prepareConfigForSave,
} from "../lib/superappConfig";

const APP_ID =
  new URLSearchParams(window.location.search).get("appId") || "Ben-test";

/* ------------------------------------------------------------------ */
/* Canonical helpers: ALWAYS use config.menu as the source of truth.   */
/* If header.primaryMenu.items / navigation exist, we clear them.      */
/* ------------------------------------------------------------------ */
function getActiveMenuList(c) {
  // Vik (Jan 16, 2026) : Prioritize primaryMenu to align with MenuEditor and PageEditor
  if (
    Array.isArray(c?.header?.primaryMenu?.items) &&
    c.header.primaryMenu.items.length > 0
  )
    return c.header.primaryMenu.items;
  if (Array.isArray(c?.menu)) return c.menu;
  if (Array.isArray(c?.navigation)) return c.navigation;
  return [];
}

function setActiveMenuList(c, items) {
  console.log("Vik (Jan 16, 2026) : setActiveMenuList called", {
    currentConfig: c,
    newItems: items,
  });

  // Vik (Jan 16, 2026) : Save to header.primaryMenu.items (Modern Standard)
  const next = {
    ...c,
    header: {
      ...(c.header || {}),
      primaryMenu: {
        ...(c.header?.primaryMenu || {}),
        items: items,
      },
    },
    // Clear legacy menu to avoid confusion/split-brain
    menu: [],
  };

  if (Array.isArray(next.navigation)) next.navigation = [];

  console.log("Vik (Jan 16, 2026) : setActiveMenuList result", next);
  return next;
}

const toKey = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");
const sig = (m) =>
  `${String(m.key || "").toLowerCase()}|${String(
    m.label || ""
  ).toLowerCase()}|${String(m.path || m.url || "").toLowerCase()}`;

/* ------------------------------------------------------------------ */

export default function NewMenu() {
  // Fast initial snapshot from cache; hydrate from backend after mount.
  const [cfg, setCfg] = useState(() => {
    const raw = getSuperAppConfigSync(APP_ID);
    return (raw && (raw.config || raw)) || { headers: {}, menu: [] };
  });

  const [title, setTitle] = useState(cfg?.headers?.title || "");
  const [label, setLabel] = useState("");
  const [path, setPath] = useState("");
  const [order, setOrder] = useState((getActiveMenuList(cfg)?.length || 0) + 1);
  const [visible, setVisible] = useState(true);
  const [status, setStatus] = useState("");

  // Hydrate from backend once
  useEffect(() => {
    loadSuperAppConfig(APP_ID)
      .then((j) => {
        const normalized = (j && (j.config || j)) || {};
        setCfg(normalized);
        setTitle(normalized?.headers?.title || "");
        setOrder((getActiveMenuList(normalized)?.length || 0) + 1);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function saveConfig(next) {
    try {
      const prepared = prepareConfigForSave(next);
      console.log("PUTTING config:", prepared);
      const res = await fetch(
        `/api/superapp/config/${encodeURIComponent(APP_ID)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ config: prepared }),
        }
      );
      console.log("PUT response status:", res.status);
      const text = await res.text();
      console.log("PUT raw response:", text);
      if (!res.ok) throw new Error("PUT failed");
      const saved = JSON.parse(text);
      const finalCfg = saved.config || saved;

      setSuperAppConfig(APP_ID, finalCfg);
      setCfg(finalCfg);
      setStatus("Saved to server ✔");

      // Vik (Jan 15, 2026) : Notify other components (like Header) of the update so they re-render immediately
      window.dispatchEvent(
        new CustomEvent("superapp-config-updated", {
          detail: { appId: APP_ID },
        })
      );
    } catch {
      // local fallback
      setSuperAppConfig(APP_ID, next);
      setCfg(next);
      setStatus("Saved locally (no PUT). Header and menu should update.");
    } finally {
      // tell Header to refresh immediately
      window.dispatchEvent(
        new CustomEvent("superapp-config-updated", {
          detail: { appId: APP_ID },
        })
      );
    }
  }

  /* -------------------- Save site title -------------------- */
  async function onSaveTitle(e) {
    e.preventDefault();
    const t = title.trim();
    if (!t) {
      setStatus("Please enter a title.");
      return;
    }

    const nextHeaders = { ...(cfg.headers || {}), title: t };

    const key = toKey(t);
    const list = getActiveMenuList(cfg);
    const exists = list.some((m) => {
      const mk = toKey(m.label || m.key || "");
      const pk = m.path ? toKey(String(m.path).replace(/^\//, "")) : "";
      return mk === key || pk === key;
    });

    let updatedList = [...list];
    if (!exists) {
      updatedList.push({
        key,
        label: t,
        order: Number((list?.length || 0) + 1),
        visible: true,
        path: `/${key}`,
      });
    }

    let next = setActiveMenuList(cfg, updatedList);
    next = { ...next, headers: nextHeaders };

    await saveConfig(next);
    if (!exists) setOrder((updatedList.length || 0) + 1);
  }

  /* -------------------- Add a menu item -------------------- */
  async function onAddMenu(e) {
    e.preventDefault();
    const lbl = label.trim();
    if (!lbl) {
      setStatus("Please enter a label.");
      return;
    }

    const key = toKey(lbl);

    // Normalize path/URL: "Test" -> "/test", keep http(s) as url.
    const raw = (path || "").trim();
    let pathOrUrl;
    if (raw && /^https?:\/\//i.test(raw)) {
      pathOrUrl = { url: raw, target: "_blank" };
    } else {
      const normalized = `/${toKey(raw.replace(/^\//, "")) || key}`;
      pathOrUrl = { path: normalized };
    }

    const item = {
      key,
      label: lbl,
      order: Number(order) || 0,
      visible: !!visible,
      ...pathOrUrl,
    };

    const list = getActiveMenuList(cfg);
    const nextList = [...list, item];

    const next = setActiveMenuList(cfg, nextList);
    await saveConfig(next);

    // reset form
    setLabel("");
    setPath("");
    setOrder((nextList.length || 0) + 1);
    setVisible(true);
  }

  /* -------------------- Delete a menu item (hard delete) -------------------- */
  async function onDeleteMenuItem(sigToDelete) {
    const active = getActiveMenuList(cfg);
    const cleanedActive = active.filter((m) => sig(m) !== sigToDelete);

    let next = setActiveMenuList(cfg, cleanedActive);
    await saveConfig(next);

    setStatus("Saved to server ✔");
  }

  const currentMenu = getActiveMenuList(cfg) || [];

  return (
    <div className="new-menu-container">
      <div className="custom-container">
        <div className="header-section">
          <h1 className="gradient-text">New Menu / Header Editor</h1>
          <p className="text-muted">
            Manage your application's header title and menu items.
          </p>
        </div>

        <div className="row">
          <div className="col-md-4">
            {/* Header title editor */}
            <div className="editor-card h-100">
              <div className="card-header">
                <h3 className="card-title">
                  <FaLayerGroup className="icon" /> Header Title
                </h3>
              </div>
              <div className="card-body">
                <form onSubmit={onSaveTitle}>
                  <div className="form-group mb-3">
                    <label className="form-label">Site Title</label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Site title"
                      className="premium-input"
                    />
                    <small className="text-muted mt-1 d-block">
                      This sets the main title in the header.
                    </small>
                  </div>
                  <button
                    type="submit"
                    className="primary-gradient-btn w-100 justify-content-center"
                  >
                    <FaSave /> Save Title
                  </button>
                </form>
              </div>
            </div>
          </div>

          <div className="col-md-4">
            {/* Add new menu item */}
            <div className="editor-card h-100">
              <div className="card-header">
                <h3 className="card-title">
                  <FaPlus className="icon" /> Add Menu Item
                </h3>
              </div>
              <div className="card-body">
                <form onSubmit={onAddMenu}>
                  <div className="form-group mb-3">
                    <label className="form-label">Label</label>
                    <input
                      type="text"
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                      placeholder="e.g., New Menu"
                      className="premium-input"
                    />
                  </div>

                  <div className="form-group mb-3">
                    <label className="form-label">Path or URL (optional)</label>
                    <input
                      type="text"
                      value={path}
                      onChange={(e) => setPath(e.target.value)}
                      placeholder="e.g., /newmenu"
                      className="premium-input"
                    />
                  </div>

                  <div className="row mb-4">
                    <div className="col-6">
                      <label className="form-label">Order</label>
                      <input
                        type="number"
                        value={order}
                        onChange={(e) => setOrder(e.target.value)}
                        className="premium-input"
                      />
                    </div>
                    <div className="col-6 d-flex align-items-end">
                      <label className="checkbox-label mb-2">
                        <input
                          type="checkbox"
                          checked={visible}
                          onChange={(e) => setVisible(e.target.checked)}
                        />
                        Visible
                      </label>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="primary-gradient-btn w-100 justify-content-center"
                  >
                    <FaPlus /> Create Menu Item
                  </button>
                </form>
              </div>
            </div>
          </div>

          <div className="col-md-4">
            {/* Current menu list */}
            <div className="editor-card h-100">
              <div className="card-header">
                <h3 className="card-title">
                  <FaList className="icon" /> Current Menu
                </h3>
              </div>
              <div className="card-body">
                {status && (
                  <div
                    className={`status-message ${
                      status.includes("✔") ? "success" : "info"
                    } mb-3`}
                  >
                    {status}
                  </div>
                )}

                {currentMenu.length === 0 ? (
                  <div className="text-center text-muted py-5">
                    <p>No menu items found.</p>
                  </div>
                ) : (
                  <div className="menu-list">
                    {currentMenu.map((item) => {
                      const id = sig(item);
                      return (
                        <div key={id} className="menu-item">
                          <div className="menu-item-content">
                            <span className="menu-item-label">
                              {item.label}
                            </span>
                            <span className="menu-item-path">
                              {item.path ? (
                                item.path
                              ) : item.url ? (
                                item.url
                              ) : (
                                <span className="text-muted">No path</span>
                              )}
                            </span>
                          </div>
                          <button
                            onClick={() => onDeleteMenuItem(id)}
                            className="icon-btn delete-btn"
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Vik (Jan 14, 2026) : Using CSS variables (var(--bg-color), etc.) here is critical.
          It allows the menu to automatically adapt to the user's selected theme without code changes. */}
      <style>{`
        .new-menu-container {
          min-height: 100vh;
          background: var(--bg-color);
          font-family: 'Inter', sans-serif;
          color: var(--text-color);
          padding: 40px 0;
        }
        .custom-container {
          max-width: 1400px; /* Increased further for 3 columns */
          margin: 0 auto;
          padding: 0 20px;
        }
        .header-section {
          margin-bottom: 40px;
          text-align: center;
        }
        .gradient-text {
          background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          font-weight: 800;
          font-size: 2.5rem;
          margin: 0 0 10px 0;
        }
        
        /* Cards */
        .editor-card {
          background: var(--surface-color);
          border-radius: 16px;
          border: 1px solid rgba(255,255,255,0.05);
          box-shadow: 0 4px 20px rgba(0,0,0,0.2);
          overflow: hidden;
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        .card-header {
          padding: 20px 24px;
          border-bottom: 1px solid rgba(255,255,255,0.05);
          background: rgba(255,255,255,0.02);
        }
        .card-title {
          margin: 0;
          font-size: 1.1rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 10px;
          color: var(--text-color);
        }
        .card-body {
          padding: 24px;
          flex: 1;
        }
        
        /* Inputs */
        .form-group {
          margin-bottom: 16px;
        }
        .form-label {
          display: block;
          margin-bottom: 8px;
          font-weight: 500;
          font-size: 0.9rem;
          color: var(--text-secondary-color, rgba(255,255,255,0.8));
        }
        .premium-input {
          width: 100%;
          padding: 12px 16px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 8px;
          color: var(--text-color);
          font-size: 14px;
          transition: all 0.2s;
        }
        .premium-input:focus {
          outline: none;
          border-color: var(--secondary-color);
          background: rgba(255,255,255,0.08);
          box-shadow: 0 0 0 3px rgba(118, 75, 162, 0.1);
        }
        .checkbox-label {
          display: flex;
          gap: 10px;
          align-items: center;
          color: var(--text-color);
          cursor: pointer;
        }
        
        /* Buttons */
        .primary-gradient-btn {
          background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .primary-gradient-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(118, 75, 162, 0.3);
        }
        .icon-btn {
          background: rgba(255,255,255,0.05);
          border: none;
          padding: 8px;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .delete-btn { color: #ff6b6b; }
        .delete-btn:hover { background: rgba(255, 107, 107, 0.1); }
        
        /* List */
        .menu-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
          max-height: 500px;
          overflow-y: auto;
        }
        .menu-item {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.05);
          border-radius: 8px;
          padding: 12px 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          transition: all 0.2s;
        }
        .menu-item:hover {
          background: rgba(255,255,255,0.05);
          transform: translateX(2px);
        }
        .menu-item-content {
          display: flex;
          flex-direction: column;
        }
        .menu-item-label {
          font-weight: 600;
          color: var(--text-color);
        }
        .menu-item-path {
          font-size: 0.85rem;
          color: var(--text-secondary-color, rgba(255,255,255,0.5));
        }
        
        /* Utils */
        .row { display: flex; flex-wrap: wrap; margin: 0 -12px; }
        .col-md-4 { width: 100%; padding: 0 12px; box-sizing: border-box; }
        .col-6 { width: 50%; padding: 0 12px; box-sizing: border-box; }
        .mb-2 { margin-bottom: 8px; }
        .mb-3 { margin-bottom: 16px; }
        .mb-4 { margin-bottom: 24px; }
        .mt-1 { margin-top: 4px; }
        .d-block { display: block; }
        .d-flex { display: flex; }
        .align-items-end { align-items: flex-end; }
        .justify-content-center { justify-content: center; }
        .w-100 { width: 100%; }
        .h-100 { height: 100%; }
        .text-muted { color: var(--text-secondary-color, rgba(255,255,255,0.5)); }
        .text-center { text-align: center; }
        .py-5 { padding-top: 48px; padding-bottom: 48px; }
        .icon { font-size: 1.1em; }
        .status-message {
          padding: 12px;
          border-radius: 8px;
          font-size: 0.9rem;
          text-align: center;
        }
        .status-message.success {
          background: rgba(74, 222, 128, 0.1);
          color: #4ade80;
          border: 1px solid rgba(74, 222, 128, 0.2);
        }
        .status-message.info {
          background: rgba(102, 126, 234, 0.1);
          color: var(--primary-color);
          border: 1px solid rgba(102, 126, 234, 0.2);
        }

        /* Responsive Media Queries */
        @media (min-width: 992px) {
          .col-md-4 { width: 33.333%; }
        }
      `}</style>
    </div>
  );
}
